import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { logger } from '../../lib/logger';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Bell, BellOff, MessageCircle, Mail, StickyNote, ChevronDown, History, FileDown, X, Filter, CheckSquare, Square } from 'lucide-react';

const POLL_MS = 20000;

function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o1 = ctx.createOscillator();
    const o2 = ctx.createOscillator();
    const g = ctx.createGain();
    o1.frequency.value = 880; o2.frequency.value = 1320;
    o1.type = 'sine'; o2.type = 'sine';
    o1.connect(g); o2.connect(g); g.connect(ctx.destination);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.8);
    o1.start(ctx.currentTime); o2.start(ctx.currentTime + 0.15);
    o1.stop(ctx.currentTime + 0.5); o2.stop(ctx.currentTime + 0.8);
  } catch (err) {
    logger.warn('[QuotesTab] Chime audio failed:', err?.message);
  }
}

function renderTemplate(tpl, q) {
  const productList = (q.products_info || []).map(p => `${p.name} (${p.sku})`).join(', ') || 'our products';
  const vars = {
    contact_name: q.contact_name || q.company_name || 'there',
    company: q.company_name || '',
    country: q.country || '',
    product_list: productList,
  };
  const substitute = (s) => Object.keys(vars).reduce((acc, k) => acc.replaceAll(`{{${k}}}`, vars[k]), s || '');
  return { subject: substitute(tpl.subject), body: substitute(tpl.body) };
}

function defaultReplyBody(q) {
  const productList = (q.products_info || []).map(p => `• ${p.name} (${p.sku})`).join('\n');
  return `Hello ${q.contact_name || q.company_name},

Thank you for reaching out regarding your interest in our Italian keratin${productList ? ':\n' + productList : ''}.

Our team is preparing a personalized quote for you and will send full details within 24 business hours.

Best regards,
Cosmetissage Keratin — Made in Italy`;
}

function QuoteCard({ q, templates, onReload, selected, onToggleSelect }) {
  const [note, setNote] = useState(q.internal_note || '');
  const [savingNote, setSavingNote] = useState(false);
  const [showNote, setShowNote] = useState(!!q.internal_note);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { setNote(q.internal_note || ''); }, [q.internal_note, q.id]);

  const saveNote = async () => {
    if (note === (q.internal_note || '')) return;
    setSavingNote(true);
    try {
      await api.patch(`/admin/quote-requests/${q.id}/note`, { internal_note: note });
      toast.success('Note saved');
      onReload();
    } catch (err) { logger.warn('[QuotesTab] Save note failed:', err?.message); toast.error('Failed to save'); }
    finally { setSavingNote(false); }
  };

  const setStatus = async (status) => {
    await api.patch(`/admin/quote-requests/${q.id}`, { status });
    toast.success('Status updated');
    onReload();
  };

  const cleanPhone = (q.phone || '').replace(/[^\d+]/g, '');
  const openReply = (channel, tpl) => {
    const content = tpl ? renderTemplate(tpl, q) : { subject: `Re: Your quote request — ${q.company_name}`, body: defaultReplyBody(q) };
    if (channel === 'email') {
      window.location.href = `mailto:${q.email}?subject=${encodeURIComponent(content.subject)}&body=${encodeURIComponent(content.body)}`;
    } else if (channel === 'whatsapp' && cleanPhone) {
      const url = `https://wa.me/${cleanPhone.replace(/^\+/, '')}?text=${encodeURIComponent(content.body)}`;
      window.open(url, '_blank');
    }
  };

  const history = q.history || [];

  return (
    <div data-testid={`quote-${q.id}`} className={`border rounded-sm p-6 bg-white transition-all ${selected ? 'border-[#C5A059] ring-1 ring-[#C5A059]' : 'border-[#E5D9C8]'}`}>
      <div className="flex items-start justify-between gap-4">
        <button data-testid={`quote-select-${q.id}`} onClick={() => onToggleSelect(q.id)}
          className="mt-1 text-[#C5A059] hover:text-[#A88544] transition-colors flex-shrink-0">
          {selected ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5 text-[#3D2817]/30" />}
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-serif text-xl">{q.company_name}</h3>
            <Badge className={
              q.status === 'new' ? 'bg-blue-100 text-blue-800' :
              q.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }>{q.status}</Badge>
          </div>
          <div className="text-sm text-[#3D2817]/60 space-x-3">
            <span>{q.contact_name}</span>
            <span>· {q.country}</span>
            <span>· <a href={`mailto:${q.email}`} className="underline">{q.email}</a></span>
            {q.phone && <span>· {q.phone}</span>}
          </div>
          {q.products_info?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {q.products_info.map(p => <Badge key={p.sku} variant="outline" className="text-xs">{p.sku} — {p.name}</Badge>)}
            </div>
          )}
          {q.estimated_quantity && <div className="mt-3 text-sm"><strong>Qty:</strong> {q.estimated_quantity}</div>}
          {q.notes && <div className="mt-2 text-sm text-[#3D2817]/70"><em>"{q.notes}"</em></div>}
          <div className="mt-3 text-xs text-[#3D2817]/40">{new Date(q.created_at).toLocaleString()}</div>

          {/* Actions row */}
          <div className="mt-4 flex flex-wrap gap-2">
            {/* Email reply w/ templates */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid={`reply-email-${q.id}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-widest border border-[#E5D9C8] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors">
                  <Mail className="w-3.5 h-3.5" /> Reply Email <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Choose template</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => openReply('email')}>Default reply</DropdownMenuItem>
                <DropdownMenuSeparator />
                {templates.length === 0 && <DropdownMenuItem disabled className="text-xs italic">No templates saved</DropdownMenuItem>}
                {templates.map(tpl => (
                  <DropdownMenuItem key={tpl.id} onClick={() => openReply('email', tpl)}>{tpl.name}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* WhatsApp reply — only if phone */}
            {cleanPhone && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid={`reply-wa-${q.id}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-widest border border-[#E5D9C8] hover:border-[#25D366] hover:text-[#25D366] transition-colors">
                    <MessageCircle className="w-3.5 h-3.5" /> Reply WhatsApp <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel>Choose template</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => openReply('whatsapp')}>Default reply</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {templates.map(tpl => (
                    <DropdownMenuItem key={tpl.id} onClick={() => openReply('whatsapp', tpl)}>{tpl.name}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <button data-testid={`toggle-note-${q.id}`} onClick={() => setShowNote(s => !s)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${
                (q.internal_note || note) ? 'border-[#C5A059] text-[#C5A059]' : 'border-[#E5D9C8] hover:border-[#3D2817]'
              }`}>
              <StickyNote className="w-3.5 h-3.5" /> {(q.internal_note || note) ? 'Edit Note' : 'Add Note'}
            </button>

            <button data-testid={`toggle-history-${q.id}`} onClick={() => setShowHistory(s => !s)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-widest border border-[#E5D9C8] hover:border-[#3D2817] transition-colors">
              <History className="w-3.5 h-3.5" /> Timeline ({history.length})
            </button>
          </div>

          {showNote && (
            <div className="mt-3">
              <Textarea data-testid={`internal-note-${q.id}`} rows={2} placeholder="Private note (not visible to buyer)…"
                value={note} onChange={e => setNote(e.target.value)} onBlur={saveNote}
                className="bg-[#FBF8F3] text-sm" />
              <div className="text-xs text-[#3D2817]/40 mt-1">{savingNote ? 'Saving…' : 'Auto-saves on blur'}</div>
            </div>
          )}

          {showHistory && (
            <div data-testid={`history-${q.id}`} className="mt-3 border-l-2 border-[#C5A059] pl-4 space-y-2">
              {history.length === 0 && <div className="text-xs text-[#3D2817]/50">No history yet.</div>}
              {[...history].reverse().map(h => (
                <div key={`${h.at}-${h.action}`} className="text-xs">
                  <div className="text-[#3D2817]/40">{new Date(h.at).toLocaleString()}</div>
                  {h.action === 'created' && <div>📨 Quote created</div>}
                  {h.action === 'status_change' && (
                    <div>🔄 Status <strong>{h.from || 'new'}</strong> → <strong>{h.to}</strong> by {h.by}</div>
                  )}
                  {h.action === 'note_edit' && (
                    <div>✏️ Note edited by {h.by}: <em className="text-[#3D2817]/60">"{h.preview}"</em></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <Select value={q.status} onValueChange={setStatus}>
          <SelectTrigger data-testid={`quote-status-${q.id}`} className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export default function QuotesTab({ quotes, onReload }) {
  const [alertEnabled, setAlertEnabled] = useState(() => localStorage.getItem('quote_alerts') !== 'off');
  const [templates, setTemplates] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCountry, setFilterCountry] = useState('all');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const lastCountRef = useRef(quotes.length);
  const initialized = useRef(false);

  useEffect(() => {
    api.get('/admin/reply-templates')
      .then(r => setTemplates(r.data))
      .catch(err => logger.warn('[QuotesTab] Failed to load templates:', err?.message));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => onReload(), POLL_MS);
    return () => clearInterval(interval);
  }, [onReload]);

  useEffect(() => {
    if (!initialized.current) { initialized.current = true; lastCountRef.current = quotes.length; return; }
    if (quotes.length > lastCountRef.current && alertEnabled) {
      const diff = quotes.length - lastCountRef.current;
      playChime();
      toast.success(`${diff} new quote request${diff > 1 ? 's' : ''} received`, { duration: 6000 });
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification('New Quote Request', { body: `${diff} new inquiry`, tag: 'quote-alert' });
        } catch (err) {
          logger.warn('[QuotesTab] Notification failed:', err?.message);
        }
      }
    }
    lastCountRef.current = quotes.length;
  }, [quotes.length, alertEnabled]);

  const toggleAlerts = async () => {
    const next = !alertEnabled;
    setAlertEnabled(next);
    localStorage.setItem('quote_alerts', next ? 'on' : 'off');
    if (next && 'Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  };

  const countries = useMemo(() => Array.from(new Set(quotes.map(q => q.country).filter(Boolean))).sort(), [quotes]);

  const filtered = useMemo(() => quotes.filter(q => {
    if (filterStatus !== 'all' && q.status !== filterStatus) return false;
    if (filterCountry !== 'all' && q.country !== filterCountry) return false;
    if (filterFrom && q.created_at < filterFrom) return false;
    if (filterTo && q.created_at > `${filterTo}T23:59:59Z`) return false;
    if (filterSearch) {
      const term = filterSearch.toLowerCase();
      const hay = `${q.company_name} ${q.contact_name} ${q.email} ${q.notes || ''} ${q.internal_note || ''}`.toLowerCase();
      if (!hay.includes(term)) return false;
    }
    return true;
  }), [quotes, filterStatus, filterCountry, filterFrom, filterTo, filterSearch]);

  const clearFilters = () => {
    setFilterStatus('all'); setFilterCountry('all');
    setFilterFrom(''); setFilterTo(''); setFilterSearch('');
  };
  const hasFilters = filterStatus !== 'all' || filterCountry !== 'all' || filterFrom || filterTo || filterSearch;

  const exportCsv = async (idsOnly = false) => {
    try {
      const params = idsOnly && selectedIds.size > 0 ? { params: { ids: Array.from(selectedIds).join(',') } } : {};
      const resp = await api.get('/admin/quote-requests/export', { ...params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `quotes-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) { logger.warn('[QuotesTab] Export failed:', err?.message); toast.error('Export failed'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(q => q.id)));
  };
  const applyBulkStatus = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    try {
      const { data } = await api.patch('/admin/quote-requests/bulk-status', { ids: Array.from(selectedIds), status: bulkStatus });
      toast.success(`Updated ${data.updated} quote${data.updated !== 1 ? 's' : ''}`);
      setSelectedIds(new Set());
      setBulkStatus('');
      onReload();
    } catch (err) { logger.warn('[QuotesTab] Bulk update failed:', err?.message); toast.error('Bulk update failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-serif">Quote Requests</h2>
        <div className="flex gap-2">
          <button data-testid="export-quotes" onClick={() => exportCsv(false)}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest border border-[#E5D9C8] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors">
            <FileDown className="w-4 h-4" /> Export CSV
          </button>
          <button data-testid="toggle-alerts" onClick={toggleAlerts}
            className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest border transition-all ${
              alertEnabled ? 'bg-[#C5A059] text-white border-[#C5A059]' : 'border-[#E5D9C8] hover:border-[#3D2817]'
            }`}>
            {alertEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
            {alertEnabled ? 'Alerts on' : 'Alerts off'}
          </button>
        </div>
      </div>

      {/* FILTERS */}
      <div className="mb-6 p-4 bg-[#F2ECE0]/50 border border-[#E5D9C8] rounded-sm">
        <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-[#3D2817]/60">
          <Filter className="w-3.5 h-3.5" /> Filters {hasFilters && <Badge variant="outline" className="text-[10px]">{filtered.length} / {quotes.length}</Badge>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#3D2817]/50">Search</label>
            <Input data-testid="filter-search" placeholder="Company, email, note…" value={filterSearch} onChange={e => setFilterSearch(e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#3D2817]/50">Status</label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#3D2817]/50">Country</label>
            <Select value={filterCountry} onValueChange={setFilterCountry}>
              <SelectTrigger data-testid="filter-country"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#3D2817]/50">From</label>
            <Input data-testid="filter-from" type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#3D2817]/50">To</label>
            <Input data-testid="filter-to" type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} />
          </div>
        </div>
        {hasFilters && (
          <button data-testid="clear-filters" onClick={clearFilters} className="mt-3 text-xs text-[#C5A059] hover:underline inline-flex items-center gap-1">
            <X className="w-3 h-3" /> Clear all filters
          </button>
        )}
      </div>

      <div className="space-y-4">
        {filtered.length > 0 && (
          <div className="flex items-center gap-3 py-3 px-4 bg-[#F2ECE0]/50 border border-[#E5D9C8] rounded-sm">
            <button data-testid="select-all" onClick={toggleSelectAll} className="text-[#C5A059] hover:text-[#A88544]">
              {selectedIds.size === filtered.length && filtered.length > 0 ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5 text-[#3D2817]/40" />}
            </button>
            <span className="text-xs uppercase tracking-widest text-[#3D2817]/60">
              {selectedIds.size === 0 ? 'Select all visible' : `${selectedIds.size} selected`}
            </span>
            {selectedIds.size > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger data-testid="bulk-status" className="w-40 h-8 text-xs"><SelectValue placeholder="Set status…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <button data-testid="bulk-apply" onClick={applyBulkStatus} disabled={!bulkStatus}
                  className="px-3 py-1.5 text-xs uppercase tracking-widest bg-[#3D2817] text-white hover:bg-[#C5A059] disabled:opacity-40 transition-colors">
                  Apply
                </button>
                <button data-testid="bulk-export" onClick={() => exportCsv(true)}
                  className="px-3 py-1.5 text-xs uppercase tracking-widest border border-[#E5D9C8] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors">
                  Export {selectedIds.size}
                </button>
                <button onClick={() => setSelectedIds(new Set())} className="text-xs text-[#3D2817]/50 hover:text-[#3D2817] px-2">Clear</button>
              </div>
            )}
          </div>
        )}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-[#3D2817]/50">
            {quotes.length === 0 ? 'No requests yet.' : 'No requests match filters.'}
          </div>
        )}
        {filtered.map(q => (
          <QuoteCard key={q.id} q={q} templates={templates} onReload={onReload}
            selected={selectedIds.has(q.id)} onToggleSelect={toggleSelect} />
        ))}
      </div>
    </div>
  );
}
